/**
 * 研究資料集頁面。
 * 對應原生版 datasets.js 全檔。
 *
 * API：
 *   GET    /api/v1/datasets                           — 資料集清單
 *   POST   /api/v1/datasets                           — 建立資料集
 *   GET    /api/v1/datasets/{id}                      — 資料集詳情
 *   GET    /api/v1/datasets/{id}/exports              — 匯出紀錄
 *   POST   /api/v1/datasets/{id}/exports              — 建立匯出
 *   GET    /api/v1/datasets/exports/{eid}/download    — 下載匯出檔
 */

import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Database, Download } from 'lucide-react';
import { apiGet, apiPost } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtTime, fmtBytes } from '@/lib/format';
import { order } from '@/lib/utils';
import { EVENT_LABEL, REVIEW_OPTIONS } from '@/lib/labels';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { DataTable, type Column } from '@/components/DataTable';
import { Drawer } from '@/components/Drawer';
import type {
  Site,
  Dataset,
  DatasetState,
  DatasetDetail,
  DatasetExport,
  ExportState,
} from '@/types';

/* ── 狀態標籤與色彩 ── */
const DS_STATE_LABEL: Record<DatasetState, string> = {
  building: '建置中',
  frozen: '已凍結',
  failed: '失敗',
};
const DS_STATE_VARIANT: Record<DatasetState, 'info' | 'success' | 'danger'> = {
  building: 'info',
  frozen: 'success',
  failed: 'danger',
};

const EX_STATE_LABEL: Record<ExportState, string> = {
  queued: '排隊中',
  running: '匯出中',
  done: '完成',
  failed: '失敗',
  expired: '已過期',
};
const EX_STATE_VARIANT: Record<ExportState, 'muted' | 'info' | 'success' | 'danger'> = {
  queued: 'muted',
  running: 'info',
  done: 'success',
  failed: 'danger',
  expired: 'muted',
};

export default function DatasetsPage() {
  const qc = useQueryClient();
  const { user } = useAuth();

  /* ── 資料查詢 ── */
  const dsQ = useQuery({
    queryKey: ['datasets'],
    queryFn: () => apiGet<Dataset[]>('/api/v1/datasets'),
  });
  const datasets = dsQ.data ?? [];

  /* ── 抽屜種類 ── */
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const detailOpen = detailId !== null;

  /* ── 資料集清單欄位 ── */
  const columns: Column<Dataset>[] = [
    { key: 'name', header: '名稱', cell: (d) => d.name },
    { key: 'version', header: '版本', cell: (d) => `v${d.version}` },
    {
      key: 'state',
      header: '狀態',
      cell: (d) => (
        <Badge variant={DS_STATE_VARIANT[d.state] ?? 'muted'}>
          {DS_STATE_LABEL[d.state] ?? d.state}
        </Badge>
      ),
    },
    {
      key: 'item_count',
      header: '事件數',
      align: 'right',
      className: 'tabular',
      cell: (d) => d.item_count,
    },
    {
      key: 'file_count',
      header: '檔案數',
      align: 'right',
      className: 'tabular',
      cell: (d) => d.file_count,
    },
    { key: 'total_bytes', header: '大小', cell: (d) => fmtBytes(d.total_bytes) },
    {
      key: 'manifest',
      header: 'Manifest',
      cell: (d) => (
        <span className="font-mono text-[11.5px]">
          {d.manifest_sha256 ? d.manifest_sha256.substring(0, 12) : '—'}
        </span>
      ),
    },
    { key: 'created_by', header: '建立者', cell: (d) => d.created_by_name ?? '—' },
    {
      key: 'created_at',
      header: '建立時間',
      cell: (d) => (
        <span className="font-mono text-[11.5px] text-muted-foreground">
          {fmtTime(d.created_at)}
        </span>
      ),
    },
    {
      key: 'detail',
      header: '',
      cell: (d) => (
        <Button size="sm" variant="ghost" onClick={() => setDetailId(d.id)}>
          詳情
        </Button>
      ),
    },
  ];

  return (
    <>
      {/* 清單卡片 */}
      <Card className="reveal admin-data-card overflow-hidden" style={order(0)}>
        <CardHeader className="pb-5">
          <div>
            <CardTitle>研究資料集</CardTitle>
            <CardDescription>建立固定的事件與檔案快照，匯出 manifest 供研究追溯</CardDescription>
          </div>
          {user && user.role !== 'viewer' && (
            <Button onClick={() => setCreateOpen(true)}>
              <Database className="h-[15px] w-[15px]" />
              <span>建立資料集</span>
            </Button>
          )}
        </CardHeader>
        <DataTable<Dataset>
          rows={datasets}
          columns={columns}
          rowKey={(d) => String(d.id)}
          loading={dsQ.isLoading}
          error={dsQ.error}
          empty={user?.role === 'viewer' ? '尚無資料集。請管理員建立研究快照。' : '尚無資料集。按「建立資料集」選擇場域與事件類型，建立研究快照。'}
        />
      </Card>

      {/* 建立資料集 Drawer */}
      <CreateDrawer
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => {
          qc.invalidateQueries({ queryKey: ['datasets'] });
          setCreateOpen(false);
        }}
      />

      {/* 詳情 Drawer */}
      {detailOpen && (
        <DetailDrawer
          dsId={detailId}
          open={detailOpen}
          onOpenChange={(v) => { if (!v) setDetailId(null); }}
          userRole={user?.role ?? 'viewer'}
        />
      )}
    </>
  );
}

/* ══════════════════════════════════════════════════════
   建立資料集 Drawer
   ══════════════════════════════════════════════════════ */

function CreateDrawer({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedSites, setSelectedSites] = useState<Set<string>>(new Set());
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
  const [selectedReviews, setSelectedReviews] = useState<Set<string>>(new Set());
  const [onlyStored, setOnlyStored] = useState(true);
  const [formError, setFormError] = useState('');

  /* 場域清單 */
  const sitesQ = useQuery({
    queryKey: ['sites'],
    queryFn: () => apiGet<Site[]>('/api/v1/sites'),
    enabled: open,
  });
  const sites = sitesQ.data ?? [];

  const createMut = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPost<Dataset>('/api/v1/datasets', body),
    onSuccess: () => {
      resetForm();
      onCreated();
    },
    onError: (e: Error) => setFormError(e.message),
  });

  const resetForm = () => {
    setName('');
    setDescription('');
    setSelectedSites(new Set());
    setSelectedTypes(new Set());
    setSelectedReviews(new Set());
    setOnlyStored(true);
    setFormError('');
  };

  const toggleSet = useCallback(
    (setter: React.Dispatch<React.SetStateAction<Set<string>>>, key: string) => {
      setter((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    [],
  );

  const handleSubmit = () => {
    if (!name.trim()) {
      setFormError('請輸入名稱');
      return;
    }
    setFormError('');
    createMut.mutate({
      name: name.trim(),
      description: description.trim() || null,
      selection: {
        site_codes: [...selectedSites],
        event_types: [...selectedTypes],
        review_states: [...selectedReviews],
        only_stored: onlyStored,
      },
    });
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange} title="建立資料集">
      <div className="flex flex-col gap-5">
        {/* 名稱 */}
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">名稱</span>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="例如：鳥類辨識-SITE-A-2026Q3"
          />
        </label>

        {/* 描述 */}
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">描述</span>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="選填"
          />
        </label>

        {/* 場域（多選 pill） */}
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">場域（可多選）</span>
          <div className="flex flex-wrap gap-2">
            {sites.map((s) => (
              <Button
                key={s.code}
                size="sm"
                type="button"
                variant={selectedSites.has(s.code) ? 'default' : 'secondary'}
                aria-pressed={selectedSites.has(s.code)}
                onClick={() => toggleSet(setSelectedSites, s.code)}
              >
                {s.code}
              </Button>
            ))}
          </div>
        </div>

        {/* 事件類型（多選 pill） */}
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">事件類型（可多選）</span>
          <div className="flex flex-wrap gap-2">
            {Object.entries(EVENT_LABEL).map(([type, label]) => (
              <Button
                key={type}
                size="sm"
                type="button"
                variant={selectedTypes.has(type) ? 'default' : 'secondary'}
                aria-pressed={selectedTypes.has(type)}
                onClick={() => toggleSet(setSelectedTypes, type)}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>

        {/* 判讀狀態（多選 pill） */}
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">判讀狀態（可多選）</span>
          <div className="flex flex-wrap gap-2">
            {REVIEW_OPTIONS.map((r) => (
              <Button
                key={r.key}
                size="sm"
                type="button"
                variant={selectedReviews.has(r.key) ? 'default' : 'secondary'}
                aria-pressed={selectedReviews.has(r.key)}
                onClick={() => toggleSet(setSelectedReviews, r.key)}
              >
                {r.label}
              </Button>
            ))}
          </div>
        </div>

        {/* 只含已保存 */}
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={onlyStored}
            onChange={(e) => setOnlyStored(e.target.checked)}
          />
          <span>只含已保存檔案</span>
        </label>

        {/* 錯誤訊息 */}
        {formError && (
          <p className="text-[13px] text-destructive">{formError}</p>
        )}

        <Button onClick={handleSubmit} disabled={createMut.isPending} block>
          {createMut.isPending ? '建立中…' : '建立'}
        </Button>
      </div>
    </Drawer>
  );
}

/* ══════════════════════════════════════════════════════
   資料集詳情 Drawer（含匯出紀錄）
   ══════════════════════════════════════════════════════ */

function DetailDrawer({
  dsId,
  open,
  onOpenChange,
  userRole,
}: {
  dsId: number;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  userRole: string;
}) {
  const qc = useQueryClient();

  /* 詳情 */
  const detailQ = useQuery({
    queryKey: ['datasets', dsId],
    queryFn: () => apiGet<DatasetDetail>(`/api/v1/datasets/${dsId}`),
    enabled: open,
  });

  /* 匯出紀錄 */
  const exportsQ = useQuery({
    queryKey: ['datasets', dsId, 'exports'],
    queryFn: () => apiGet<DatasetExport[]>(`/api/v1/datasets/${dsId}/exports`),
    enabled: open,
    refetchInterval: (query) => query.state.data?.some((ex) => ex.state === 'queued' || ex.state === 'running') ? 4000 : false,
  });

  /* 建立匯出 */
  const exportMut = useMutation({
    mutationFn: (format: string) =>
      apiPost<DatasetExport>(`/api/v1/datasets/${dsId}/exports`, { format }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['datasets', dsId, 'exports'] });
    },
  });

  const ds = detailQ.data?.dataset;
  const stats = detailQ.data?.stats;
  const exports = exportsQ.data ?? [];

  if (!ds && detailQ.isLoading) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange} title="載入中…">
        <p className="text-muted-foreground">正在載入資料集詳情…</p>
      </Drawer>
    );
  }

  if (detailQ.error) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange} title="載入失敗">
        <p className="text-destructive">
          {detailQ.error instanceof Error ? detailQ.error.message : '未知錯誤'}
        </p>
      </Drawer>
    );
  }

  if (!ds) return null;

  /* 匯出紀錄表格欄位 */
  const expColumns: Column<DatasetExport>[] = [
    {
      key: 'format',
      header: '格式',
      cell: (ex) => (ex.format ?? '').toUpperCase(),
    },
    {
      key: 'state',
      header: '狀態',
      cell: (ex) => (
        <Badge variant={EX_STATE_VARIANT[ex.state] ?? 'muted'}>
          {EX_STATE_LABEL[ex.state] ?? ex.state}
        </Badge>
      ),
    },
    {
      key: 'size',
      header: '大小',
      cell: (ex) => fmtBytes(ex.output_bytes),
    },
    {
      key: 'created_at',
      header: '時間',
      cell: (ex) => (
        <span className="font-mono text-[11.5px] text-muted-foreground">
          {fmtTime(ex.created_at)}
        </span>
      ),
    },
    {
      key: 'download',
      header: '下載',
      cell: (ex) =>
        ex.state === 'done' ? (
          <Button asChild size="sm" variant="secondary">
            <a
              href={`/api/v1/datasets/exports/${ex.id}/download`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Download className="h-3.5 w-3.5" />
              <span>下載</span>
            </a>
          </Button>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
  ];

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={`資料集詳情 — ${ds.name} v${ds.version}`}
      wide
    >
      <div className="flex flex-col gap-4">
        {/* 基本資訊 */}
        <div className="flex items-center gap-2">
          <h3 className="text-[15px] font-semibold">{ds.name} v{ds.version}</h3>
          <Badge variant={DS_STATE_VARIANT[ds.state] ?? 'muted'}>
            {DS_STATE_LABEL[ds.state] ?? ds.state}
          </Badge>
        </div>
        {ds.description && <p className="text-[13px] text-muted-foreground">{ds.description}</p>}
        <p className="text-[13px]">
          事件 {ds.item_count} 筆 / 檔案 {ds.file_count} 個 / {fmtBytes(ds.total_bytes)}
        </p>
        {ds.frozen_at && (
          <p className="text-[12px] text-muted-foreground">凍結時間：{fmtTime(ds.frozen_at)}</p>
        )}

        {/* Manifest SHA-256 */}
        {ds.manifest_sha256 && (
          <div className="flex min-w-0 flex-wrap items-center gap-2 rounded-sm border border-border p-3">
            <span className="text-[12px]">Manifest SHA-256：</span>
            <span className="min-w-0 break-all font-mono text-[12px]">{ds.manifest_sha256}</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                void navigator.clipboard.writeText(ds.manifest_sha256 ?? '');
              }}
            >
              複製
            </Button>
          </div>
        )}

        {/* 統計分布 */}
        {stats && (
          <div className="flex flex-col gap-3">
            <StatsTable title="依事件類型" rows={stats.by_type} keyLabel="類型" valLabel="數量" />
            <StatsTable title="依判讀狀態" rows={stats.by_review} keyLabel="狀態" valLabel="數量" />
            <StatsTable title="依場域" rows={stats.by_site} keyLabel="場域" valLabel="數量" />
          </div>
        )}

        {/* 匯出按鈕（凍結狀態 + 非 viewer） */}
        {ds.state === 'frozen' && userRole !== 'viewer' && (
          <div className="flex flex-col gap-2">
            <h3 className="text-[15px] font-semibold">匯出</h3>
            <div className="flex gap-2">
              {(['json', 'csv', 'bundle'] as const).map((fmt) => (
                <Button
                  key={fmt}
                  size="sm"
                  variant="secondary"
                  disabled={exportMut.isPending}
                  onClick={() => exportMut.mutate(fmt)}
                >
                  {fmt.toUpperCase()}
                </Button>
              ))}
            </div>
            {exportMut.error && (
              <p className="text-[13px] text-destructive">
                {exportMut.error instanceof Error ? exportMut.error.message : '匯出失敗'}
              </p>
            )}
          </div>
        )}

        {/* 匯出紀錄 */}
        <div className="flex flex-col gap-2">
          <h3 className="text-[15px] font-semibold">匯出紀錄</h3>
          {exports.some((ex) => ex.state === 'queued' || ex.state === 'running') && (
            <p className="text-[12px] text-muted-foreground" role="status">匯出處理中，完成後會自動更新。</p>
          )}
          {exports.some((ex) => ex.state === 'failed') && (
            <p className="text-[12px] text-destructive">匯出失敗。可重新選擇格式建立匯出，或按下方按鈕更新紀錄。</p>
          )}
          <Button size="sm" variant="ghost" onClick={() => void exportsQ.refetch()} disabled={exportsQ.isFetching}>
            {exportsQ.isFetching ? '更新中…' : '更新匯出紀錄'}
          </Button>
          <DataTable<DatasetExport>
            rows={exports}
            columns={expColumns}
            rowKey={(ex) => String(ex.id)}
            loading={exportsQ.isLoading}
            error={exportsQ.error}
            empty="尚無匯出紀錄。"
          />
        </div>
      </div>
    </Drawer>
  );
}

/* ── 統計分布小表格 ── */
function StatsTable({
  title,
  rows,
  keyLabel,
  valLabel,
}: {
  title: string;
  rows?: Record<string, string | number>[];
  keyLabel: string;
  valLabel: string;
}) {
  if (!rows || !rows.length) return null;
  return (
    <div>
      <h4 className="mb-1 text-[13px] font-medium">{title}</h4>
      <table className="data-table w-full border-collapse text-[13px]">
        <thead>
          <tr>
            <th>{keyLabel}</th>
            <th className="!text-right">{valLabel}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const vals = Object.values(r);
            return (
              <tr key={i}>
                <td data-label={keyLabel}>{vals[0] ?? '—'}</td>
                <td data-label={valLabel} className="tabular text-right">{String(vals[1] ?? 0)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
